import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Wrench, IndianRupee, Calculator, Car, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CapturePhoto, MultiCapturePhoto } from '@/components/transport/CapturePhoto';
import { transportApi } from '@/services/transportApi';
import { KpiTile, DateRangeFilter, inDateRange, OwnershipFilter, ownershipParams, HiredBadge } from '@/components/transport/DashboardBits';
import { AddBillsDialog } from '@/components/transport/AddBillsDialog';

const TYPES = ['service', 'repair', 'tyre', 'other'];

const ServiceLog = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vehicle_id: '', date: '', odometer: '', service_type: 'service', cost: '', vendor: '' });
  const [billPhotos, setBillPhotos] = useState([]);
  const [odoPhoto, setOdoPhoto] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [own, setOwn] = useState('all');
  const filtered = useMemo(() => rows.filter(r => inDateRange(r.date, from, to)), [rows, from, to]);
  const kpis = useMemo(() => {
    const cost = filtered.reduce((s, r) => s + (Number(r.cost) || 0), 0);
    const vehicles = new Set(filtered.map(r => r.vehicle_id).filter(Boolean)).size;
    return { entries: filtered.length, cost, avg: filtered.length ? cost / filtered.length : 0, vehicles };
  }, [filtered]);

  const load = async () => {
    setRows(await transportApi.listService(ownershipParams(own)));
    setVehicles(await transportApi.listVehicles({ active: true }));
    try { setVendors(await transportApi.listVendors()); } catch { /* ignore */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => toast.error('Failed to load service log')); }, [own]);
  const [saving, setSaving] = useState(false);
  const [billsFor, setBillsFor] = useState(null);

  const save = async () => {
    if (!form.vehicle_id || !form.cost) { toast.error('Vehicle & cost required'); return; }
    setSaving(true);
    try {
      await transportApi.createService({ ...form, odometer: form.odometer ? Number(form.odometer) : null, cost: Number(form.cost), bill_photos: billPhotos, odo_photo: odoPhoto });
      toast.success('Service entry added'); setOpen(false); setForm({ vehicle_id: '', date: '', odometer: '', service_type: 'service', cost: '', vendor: '' }); setBillPhotos([]); setOdoPhoto(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed — please check your connection and try again'); load(); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-service-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold font-display">Service / Maintenance Log</h1>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="service-add-btn"><Plus className="w-4 h-4 mr-1" />Add Entry</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="service-kpis">
          <KpiTile label="Entries" value={kpis.entries} icon={Wrench} accent="slate" testId="service-kpi-entries" />
          <KpiTile label="Total Cost" value={`₹${kpis.cost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} icon={IndianRupee} accent="green" testId="service-kpi-cost" />
          <KpiTile label="Avg Cost" value={`₹${kpis.avg.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} icon={Calculator} accent="amber" testId="service-kpi-avg" />
          <KpiTile label="Vehicles Serviced" value={kpis.vehicles} icon={Car} accent="blue" testId="service-kpi-vehicles" />
        </div>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(''); setTo(''); }} testPrefix="service">
          <OwnershipFilter value={own} onChange={setOwn} testPrefix="service" />
        </DateRangeFilter>
        <Card className="border-0 shadow-md"><CardContent className="p-0">
          <Table data-testid="service-table">
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Vehicle</TableHead><TableHead>Type</TableHead>
              <TableHead>Odometer</TableHead><TableHead>Cost (₹)</TableHead><TableHead>Vendor</TableHead><TableHead>Odo Photo</TableHead><TableHead>Bill(s)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id} data-testid={`service-row-${r.id}`}>
                  <TableCell>{r.date}</TableCell><TableCell className="whitespace-nowrap">{r.vehicle_label}<HiredBadge ownership={r.vehicle_ownership} testId={`service-hired-${r.id}`} /></TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{r.service_type}</Badge></TableCell>
                  <TableCell>{r.odometer ?? '—'}</TableCell><TableCell>₹{r.cost}</TableCell><TableCell>{r.vendor || '—'}</TableCell>
                  <TableCell>{r.odo_photo_url ? <a href={r.odo_photo_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs" data-testid={`service-odo-photo-${r.id}`}>View</a> : '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">{(r.bill_photo_urls && r.bill_photo_urls.length) ? r.bill_photo_urls.map((u, i) => (<a key={i} href={u} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs mr-2" data-testid={`service-bill-${r.id}-${i}`}>Bill {i + 1}</a>)) : <span className="text-gray-400 mr-2">—</span>}
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] border-amber-300" onClick={() => setBillsFor(r)} data-testid={`service-add-bills-${r.id}`}><Camera className="w-3 h-3 mr-1" />Add</Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8" data-testid="service-empty">No service entries in this range.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="service-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Add Service Entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Vehicle</Label>
              <Select value={form.vehicle_id} onValueChange={v => setForm(f => ({ ...f, vehicle_id: v }))}><SelectTrigger data-testid="service-vehicle-select"><SelectValue placeholder="Vehicle" /></SelectTrigger>
                <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration_no}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="service-date-input" /></div>
              <div><Label>Type</Label>
                <Select value={form.service_type} onValueChange={v => setForm(f => ({ ...f, service_type: v }))}><SelectTrigger data-testid="service-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Odometer (km)</Label><Input type="number" value={form.odometer} onChange={e => setForm(f => ({ ...f, odometer: e.target.value }))} data-testid="service-odo-input" /></div>
              <div><Label>Cost (₹)</Label><Input type="number" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} data-testid="service-cost-input" /></div>
            </div>
            <div><Label>Vendor</Label><Input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} list="svc-vendors" data-testid="service-vendor-input" />
              <datalist id="svc-vendors">{vendors.map(v => <option key={v.id} value={v.name} />)}</datalist></div>
            <div><Label>Odometer Photo</Label><CapturePhoto label="Photograph odometer" onCapture={setOdoPhoto} captured={!!odoPhoto} testId="service-odo-photo" /></div>
            <div><Label>Bill Photos (pages)</Label><MultiCapturePhoto label="Photograph service bill (page 1)" photos={billPhotos} onChange={setBillPhotos} testId="service-bill-photos" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="service-save-btn">{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AddBillsDialog row={billsFor} kind="service" onClose={() => setBillsFor(null)} onDone={load} />
    </div>
  );
};

export default ServiceLog;
